import React from "react";
import { Checkbox } from "components";
import { useExemptionsContext } from "../../ExemptionsContext";
import { useEventTracker } from "utils/useEventTracker";
import constants from "utils/constants";
import { usePropertyContext } from "../../context";
import ButtonV2 from "elements/ButtonV2";
import { usePropertyValueContext } from "../../PropertyValueChart";

import { ExemptionQuestions } from "utils/types/exemption-questions";

const RareSituations = () => {
	const {
		flowId,
		next,
		otherHeirOwnersOccupy,
		setOtherHeirOwnersOccupy,
		isInheritedProperty,
		setIsInheritedProperty,
		isVeteran,
		setIsVeteran,
		hasDisability,
		setHasDisability,
		survivingSpouseOf65PlusPerson,
		setSurvivingSpouseOf65PlusPerson,
		disabledVeteranOrSurvivingSpouse,
		setDisabledVeteranOrSurvivingSpouse,
		survivingSpouseOfVeteranKilledInLineOfDuty,
		setSurvivingSpouseOfVeteranKilledInLineOfDuty,
		survivingSpouseOfFirstResponderKilledInLineOfDuty,
		setSurvivingSpouseOfFirstResponderKilledInLineOfDuty,
		donatedResidenceOfPartiallyDisabledVetOrSurvivngSpouse,
		setDonatedResidenceOfPartiallyDisabledVetOrSurvivngSpouse,
		ownedThroughCooperativeHousing,
		setOwnedThroughCooperativeHousing,
		ownedThroughNonProfit,
		setOwnedThroughNonProfit,
		collectRentAtThisProperty,
		setCollectRentAtThisProperty,
		specialFieldsValue,
		setSpecialFieldsValue,
		noneOfTheAbove,
		setNoneOfTheAbove,
	} = useExemptionsContext();
	const trackEvent = useEventTracker();
	const { details } = usePropertyContext();

	const { currentMarketValue } = usePropertyValueContext();

	const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (e.target.name !== "none" && e.target.checked === true) {
			setNoneOfTheAbove(false);
		}

		if (ExemptionQuestions.RARE_SITUATION_KEYS.includes(e.target.name as any)) {
			setSpecialFieldsValue({
				[e.target.name]: e.target.checked,
			});
			return;
		}

		switch (e.target.name) {
			case "collectRent":
				setCollectRentAtThisProperty(e.target.checked);
				return;
			case "survivingSpouseDisabled":
				setDisabledVeteranOrSurvivingSpouse(e.target.checked);
				return;
			case "survivingSpouseArmedServicesFatallyInjured":
				setSurvivingSpouseOfVeteranKilledInLineOfDuty(e.target.checked);
				return;
			case "survivingSpouseFirstResponder":
				setSurvivingSpouseOfFirstResponderKilledInLineOfDuty(e.target.checked);
				return;
			case "inherited":
				setIsInheritedProperty(e.target.checked);
				if (e.target.checked === false) {
					setOtherHeirOwnersOccupy(false);
				}
				return;
			case "veteran":
				setIsVeteran(e.target.checked);
				return;
			case "otherHeirsOccupy":
				setOtherHeirOwnersOccupy(e.target.checked);
				return;
			case "donatedResidence":
				setDonatedResidenceOfPartiallyDisabledVetOrSurvivngSpouse(
					e.target.checked
				);
				return;
			case "cooperativeHousing":
				setOwnedThroughCooperativeHousing(e.target.checked);
				return;
			case "manufacturedHome":
				setSpecialFieldsValue({
					is_manufactured_home: e.target.checked,
				});
				return;
			case "nonProfitHousing":
				setOwnedThroughNonProfit(e.target.checked);
				return;
			case "survivingSpouse65Plus":
				setSurvivingSpouseOf65PlusPerson(e.target.checked);
				return;
			case "disability":
				setHasDisability(e.target.checked);
				return;
			case "none":
				setNoneOfTheAbove(e.target.checked);
				setCollectRentAtThisProperty(false);
				setDisabledVeteranOrSurvivingSpouse(false);
				setSurvivingSpouseOfVeteranKilledInLineOfDuty(false);
				setSurvivingSpouseOfFirstResponderKilledInLineOfDuty(false);
				setIsInheritedProperty(false);
				setOtherHeirOwnersOccupy(false);
				setDonatedResidenceOfPartiallyDisabledVetOrSurvivngSpouse(false);
				setOwnedThroughCooperativeHousing(false);
				setSurvivingSpouseOf65PlusPerson(false);
				setHasDisability(false);
				setIsVeteran(false);
				setOwnedThroughNonProfit(false);
				setSpecialFieldsValue({
					...ExemptionQuestions.RARE_SITUATION_KEYS.reduce(
						(acc, cur) => {
							acc[cur] = false;
							return acc;
						},
						{} as Record<ExemptionQuestions.RareSituationKey, boolean>
					),
				});
				return;
		}
	};

	const checkboxOptions = React.useMemo(() => {
		if (flowId === "TEXAS_EXEMPTION") {
			return [
				{
					label: "I have a disability.",
					name: "disability",
					state: hasDisability,
				},
				{
					label: "I collect rent at this property.",
					name: "collectRent",
					state: collectRentAtThisProperty,
				},
				{
					label: "I'm a 100% disabled veteran or surviving spouse.",
					name: "survivingSpouseDisabled",
					state: disabledVeteranOrSurvivingSpouse,
				},
				{
					label: "I'm the surviving spouse of a deceased 65+ partner.",
					name: "survivingSpouse65Plus",
					state: survivingSpouseOf65PlusPerson,
				},
				{
					label:
						"I'm the surviving spouse of an armed services member killed or fatally injured in the line of duty.",
					name: "survivingSpouseArmedServicesFatallyInjured",
					state: survivingSpouseOfVeteranKilledInLineOfDuty,
				},
				{
					label:
						"I'm the surviving spouse of first responder killed in the line of duty.",
					name: "survivingSpouseFirstResponder",
					state: survivingSpouseOfFirstResponderKilledInLineOfDuty,
				},
				{
					label: "I inherited this property.",
					name: "inherited",
					state: isInheritedProperty,
					// secondaryOption: {
					// 	label: "Other heir owners occupy this property",
					// 	name: "otherHeirsOccupy",
					// 	state: otherHeirOwnersOccupy,
					// },
				},
				{
					label:
						"Donated residence of partially disabled veteran or surviving spouse.",
					name: "donatedResidence",
					state: donatedResidenceOfPartiallyDisabledVetOrSurvivngSpouse,
				},
				{
					label: "Owned through cooperative housing.",
					name: "cooperativeHousing",
					state: ownedThroughCooperativeHousing,
				},
				{
					label: "I am currently on active duty for the armed forces.",
					name: "is_active_duty_personnel",
					state: specialFieldsValue?.is_active_duty_personnel,
				},
				{
					label: "Residence is a manufactured home.",
					name: "is_manufactured_home",
					state: specialFieldsValue?.is_manufactured_home,
				},
				{ label: "None of the above", name: "none", state: noneOfTheAbove },
			];
		}

		if (flowId === "CALIFORNIA_EXEMPTION") {
			return [
				{
					label: "I have a disability.",
					name: "disability",
					state: hasDisability,
				},
				{
					label: "I'm a veteran.",
					name: "veteran",
					state: isVeteran,
				},
				{
					label: "I inherited this property.",
					name: "inherited",
					state: isInheritedProperty,
				},
				{
					label:
						"Owned through a church, religious institution, or non-profit.",
					name: "nonProfitHousing",
					state: ownedThroughNonProfit,
				},
				{
					label: "I am currently on active duty for the armed forces.",
					name: "is_active_duty_personnel",
					state: specialFieldsValue?.is_active_duty_personnel,
				},
				{ label: "None of the above", name: "none", state: noneOfTheAbove },
			];
		}

		if (flowId === "GEORGIA_EXEMPTION") {
			return [
				{
					label: "I'm a veteran.",
					name: "veteran",
					state: isVeteran,
				},
				{
					label: "I'm the surviving spouse or child of a veteran.",
					name: "surviving_spouse_or_child_of_a_veteran",
					state: specialFieldsValue?.surviving_spouse_or_child_of_a_veteran,
				},
				{
					label:
						"I'm the surviving spouse of a peace officer or firefighter killed in the line of duty.",
					name: "survivingSpouseFirstResponder",
					state: survivingSpouseOfFirstResponderKilledInLineOfDuty,
				},
				{
					label: "I am currently on active duty for the armed forces.",
					name: "is_active_duty_personnel",
					state: specialFieldsValue?.is_active_duty_personnel,
				},
				{ label: "None of the above", name: "none", state: noneOfTheAbove },
			];
		}

		if (flowId === "NEW_YORK_EXEMPTION") {
			return [
				{
					label: "I have a disability.",
					name: "disability",
					state: hasDisability,
				},
				{
					label: "I'm a veteran.",
					name: "veteran",
					state: isVeteran,
				},
				{
					label: "Property is a part of a co-op building.",
					name: "cooperativeHousing",
					state: ownedThroughCooperativeHousing,
				},
				{
					label: "Residence is a manufactured home.",
					name: "is_manufactured_home",
					state: specialFieldsValue?.is_manufactured_home,
				},
				{
					label: "I am currently on active duty for the armed forces.",
					name: "is_active_duty_personnel",
					state: specialFieldsValue?.is_active_duty_personnel,
				},
				{ label: "None of the above", name: "none", state: noneOfTheAbove },
			];
		}

		if (flowId === "ILLINOIS_EXEMPTION") {
			return [
				{
					label: "I have a disability.",
					name: "disability",
					state: hasDisability,
				},
				{
					label: "I'm a disabled veteran.",
					name: "disabled_veteran",
					state: specialFieldsValue?.disabled_veteran,
				},
				{
					label: "I have recently returned from active duty in armed conflict.",
					name: "recently_returned_from_active_duty",
					state: specialFieldsValue?.recently_returned_from_active_duty,
				},
				{
					label:
						"I was/am a resident of a facility licensed under the Assisted Living and Shared Housing Act, Nursing Home Care Act.",
					name: "was_resident_of_assisted_living_facility",
					state: specialFieldsValue?.was_resident_of_assisted_living_facility,
				},
				{
					label: "I am currently on active duty for the armed forces.",
					name: "is_active_duty_personnel",
					state: specialFieldsValue?.is_active_duty_personnel,
				},
				{ label: "None of the above", name: "none", state: noneOfTheAbove },
			];
		}

		if (flowId === "FLORIDA_EXEMPTION") {
			return [
				{
					label: "I have a disability.",
					name: "disability",
					state: hasDisability,
				},
				{
					label: "I am a widow or widower.",
					name: "is_widow",
					state: specialFieldsValue?.is_widow,
				},
				{
					label: "I am a blind person.",
					name: "is_blind",
					state: specialFieldsValue?.is_blind,
				},
				{
					label:
						"I am a disabled first responder or a surviving spouse of a first responder.",
					name: "is_disabled_first_responder_or_surviving_spouse_of_a_first_responder",
					state:
						specialFieldsValue?.is_disabled_first_responder_or_surviving_spouse_of_a_first_responder,
				},
				{
					label:
						"I am a surviving spouse of a first responder who died in the line of duty.",
					name: "is_surviving_spouse_of_a_first_responder_who_died_in_the_line_of_duty",
					state:
						specialFieldsValue?.is_surviving_spouse_of_a_first_responder_who_died_in_the_line_of_duty,
				},
				{
					label:
						"I am a disabled veteran aged 65 or older, or a surviving spouse of a disabled veteran.",
					name: "is_a_disabled_veteran_aged_65_or_older_or_a_surviving_spouse_of_a_disabled_veteran",
					state:
						specialFieldsValue?.is_a_disabled_veteran_aged_65_or_older_or_a_surviving_spouse_of_a_disabled_veteran,
				},
				{
					label:
						"I am a disabled veteran confined to a wheelchair due to a service-connected disability.",
					name: "is_a_disabled_veteran_confined_to_a_wheelchair",
					state:
						specialFieldsValue?.is_a_disabled_veteran_confined_to_a_wheelchair,
				},
				{
					label:
						"I am a totally and permanently disabled veteran, or a surviving spouse of such a veteran.",
					name: "is_a_totally_and_permanently_disabled_veteran_or_a_surviving_spouse_of_such_a_veteran",
					state:
						specialFieldsValue?.is_a_totally_and_permanently_disabled_veteran_or_a_surviving_spouse_of_such_a_veteran,
				},
				{
					label:
						"I am a surviving spouse of a veteran who died while on active duty.",
					name: "is_a_surviving_spouse_of_a_veteran_who_died_while_on_active_duty",
					state:
						specialFieldsValue?.is_a_surviving_spouse_of_a_veteran_who_died_while_on_active_duty,
				},
				{
					label: "I am currently on active duty for the armed forces.",
					name: "is_active_duty_personnel",
					state: specialFieldsValue?.is_active_duty_personnel,
				},
				{ label: "None of the above", name: "none", state: noneOfTheAbove },
			];
		}

		if (flowId === "MICHIGAN_EXEMPTION") {
			return [
				{
					label: "I make less than $67,000 per year.",
					name: "make_less_than_67k",
					state: specialFieldsValue?.make_less_than_67k,
				},
				{
					label: "I'm 65 years or older.",
					name: "is_65_years_or_older",
					state: specialFieldsValue?.is_65_years_or_older,
				},
				{
					label: "I'm a disabled veteran.",
					name: "disabled_veteran",
					state: specialFieldsValue?.disabled_veteran,
				},
				{
					label:
						"I'm the surviving spouse of someone who served in the armed forces.",
					name: "surviving_spouse_of_a_veteran",
					state: specialFieldsValue?.surviving_spouse_of_a_veteran,
				},
				{
					label: "This is a commercial property.",
					name: "is_commercial_property",
					state: specialFieldsValue?.is_commercial_property,
				},
				{
					label: "I am currently on active duty for the armed forces.",
					name: "is_active_duty_personnel",
					state: specialFieldsValue?.is_active_duty_personnel,
				},
				{ label: "None of the above", name: "none", state: noneOfTheAbove },
			];
		}

		if (flowId === "PENNSYLVANIA_EXEMPTION") {
			return [
				{
					label: "I am a 18+ disabled person and make under $45k a year.",
					name: "is_age_18_or_older_disabled_and_under_45k_per_year",
					state:
						specialFieldsValue?.is_age_18_or_older_disabled_and_under_45k_per_year,
				},
				{
					label: "I am a low income senior 65+ that makes under $45k a year.",
					name: "is_age_65_or_older_and_under_45k_per_year",
					state: specialFieldsValue?.is_age_65_or_older_and_under_45k_per_year,
				},
				{
					label: "I'm the surviving spouse of a deceased 50+ partner.",
					name: "is_surving_spouse_of_a_deceased_partner_age_50_or_older",
					state:
						specialFieldsValue?.is_surving_spouse_of_a_deceased_partner_age_50_or_older,
				},
				{
					label: "I am currently on active duty for the armed forces.",
					name: "is_active_duty_personnel",
					state: specialFieldsValue?.is_active_duty_personnel,
				},
				{ label: "None of the above", name: "none", state: noneOfTheAbove },
			];
		}

		return [];
	}, [
		flowId,
		details?.address,
		hasDisability,
		isVeteran,
		isInheritedProperty,
		ownedThroughNonProfit,
		noneOfTheAbove,
		collectRentAtThisProperty,
		disabledVeteranOrSurvivingSpouse,
		survivingSpouseOf65PlusPerson,
		survivingSpouseOfVeteranKilledInLineOfDuty,
		survivingSpouseOfFirstResponderKilledInLineOfDuty,
		donatedResidenceOfPartiallyDisabledVetOrSurvivngSpouse,
		ownedThroughCooperativeHousing,
		specialFieldsValue,
		otherHeirOwnersOccupy,
	]);

	const convertCheckboxStatesToStringForAnalytics = () => {
		const finalOutput = [];
		if (hasDisability) {
			finalOutput.push(
				checkboxOptions.find(option => option.name === "disability")?.label
			);
		}
		if (isVeteran) {
			finalOutput.push(
				checkboxOptions.find(option => option.name === "veteran")?.label
			);
		}
		if (collectRentAtThisProperty) {
			finalOutput.push(
				checkboxOptions.find(option => option.name === "collectRent")?.label
			);
		}
		if (disabledVeteranOrSurvivingSpouse) {
			finalOutput.push(
				checkboxOptions.find(
					option => option.name === "survivingSpouseDisabled"
				)?.label
			);
		}
		if (survivingSpouseOf65PlusPerson) {
			finalOutput.push(
				checkboxOptions.find(option => option.name === "survivingSpouse65Plus")
					?.label
			);
		}
		if (survivingSpouseOfVeteranKilledInLineOfDuty) {
			finalOutput.push(
				checkboxOptions.find(
					option => option.name === "survivingSpouseArmedServicesFatallyInjured"
				)?.label
			);
		}
		if (survivingSpouseOfFirstResponderKilledInLineOfDuty) {
			finalOutput.push(
				checkboxOptions.find(
					option => option.name === "survivingSpouseFirstResponder"
				)?.label
			);
		}
		if (isInheritedProperty) {
			finalOutput.push(
				checkboxOptions.find(option => option.name === "inherited")?.label
			);
		}
		// if (otherHeirOwnersOccupy) {
		// 	finalOutput.push(
		// 		checkboxOptions.find(option => option.name === "inherited")
		// 			?.secondaryOption?.label
		// 	);
		// }
		if (donatedResidenceOfPartiallyDisabledVetOrSurvivngSpouse) {
			finalOutput.push(
				checkboxOptions.find(option => option.name === "donatedResidence")
					?.label
			);
		}
		if (ownedThroughCooperativeHousing) {
			finalOutput.push(
				checkboxOptions.find(option => option.name === "cooperativeHousing")
					?.label
			);
		}

		if (ownedThroughNonProfit) {
			finalOutput.push(
				checkboxOptions.find(option => option.name === "nonProfitHousing")
					?.label
			);
		}
		if (noneOfTheAbove) {
			finalOutput.push(
				checkboxOptions.find(option => option.name === "none")?.label
			);
		}

		for (const key of ExemptionQuestions.RARE_SITUATION_KEYS) {
			if (specialFieldsValue?.[key]) {
				finalOutput.push(
					checkboxOptions.find(option => option.name === key)?.label
				);
			}
		}

		const finalLabel = finalOutput.join(", ");
		return finalLabel;
	};

	const handleSubmit = async () => {
		trackEvent({
			eventName: constants.EVENTS.Exemptions_Flow_Other_Situations_Answer,
			data: {
				"Other Situations": convertCheckboxStatesToStringForAnalytics(),
				"Is End-To-End Signup": details?.exemption?.is_end_to_end_signup
					? "Yes"
					: "No",
			},
		});
		const trackingEventData = {
			"Other Situations": convertCheckboxStatesToStringForAnalytics(),
		};
		next({ trackingEventData });
	};

	const unableToFile = React.useMemo(() => {
		if (flowId === "CALIFORNIA_EXEMPTION") {
			return (
				hasDisability ||
				isVeteran ||
				isInheritedProperty ||
				ownedThroughNonProfit
			);
		}

		if (flowId === "GEORGIA_EXEMPTION") {
			return (
				isVeteran ||
				specialFieldsValue?.surviving_spouse_or_child_of_a_veteran ||
				survivingSpouseOfFirstResponderKilledInLineOfDuty
			);
		}

		if (flowId === "NEW_YORK_EXEMPTION") {
			return isVeteran || hasDisability;
		}

		if (flowId === "ILLINOIS_EXEMPTION") {
			return (
				hasDisability ||
				specialFieldsValue?.is_65_years_or_older ||
				specialFieldsValue?.disabled_veteran ||
				specialFieldsValue?.recently_returned_from_active_duty ||
				specialFieldsValue?.was_resident_of_assisted_living_facility
			);
		}

		if (flowId === "FLORIDA_EXEMPTION") {
			return (
				hasDisability ||
				specialFieldsValue?.is_widow ||
				specialFieldsValue?.is_blind ||
				specialFieldsValue?.is_disabled_first_responder_or_surviving_spouse_of_a_first_responder ||
				specialFieldsValue?.is_surviving_spouse_of_a_first_responder_who_died_in_the_line_of_duty ||
				specialFieldsValue?.is_a_disabled_veteran_aged_65_or_older_or_a_surviving_spouse_of_a_disabled_veteran ||
				specialFieldsValue?.is_a_disabled_veteran_confined_to_a_wheelchair ||
				specialFieldsValue?.is_a_totally_and_permanently_disabled_veteran_or_a_surviving_spouse_of_such_a_veteran ||
				specialFieldsValue?.is_a_surviving_spouse_of_a_veteran_who_died_while_on_active_duty
			);
		}

		if (flowId === "MICHIGAN_EXEMPTION") {
			return (
				(specialFieldsValue?.make_less_than_67k &&
					currentMarketValue &&
					currentMarketValue > 154_000) ||
				specialFieldsValue?.is_65_years_or_older ||
				specialFieldsValue?.disabled_veteran ||
				specialFieldsValue?.surviving_spouse_of_a_veteran ||
				specialFieldsValue?.is_commercial_property
			);
		}

		if (flowId === "PENNSYLVANIA_EXEMPTION") {
			return false;
		}

		return (
			hasDisability ||
			disabledVeteranOrSurvivingSpouse ||
			survivingSpouseOf65PlusPerson ||
			survivingSpouseOfVeteranKilledInLineOfDuty ||
			survivingSpouseOfFirstResponderKilledInLineOfDuty ||
			isInheritedProperty ||
			donatedResidenceOfPartiallyDisabledVetOrSurvivngSpouse ||
			ownedThroughCooperativeHousing ||
			specialFieldsValue?.is_manufactured_home
		);
	}, [
		flowId,
		hasDisability,
		isVeteran,
		disabledVeteranOrSurvivingSpouse,
		survivingSpouseOf65PlusPerson,
		survivingSpouseOfVeteranKilledInLineOfDuty,
		survivingSpouseOfFirstResponderKilledInLineOfDuty,
		isInheritedProperty,
		donatedResidenceOfPartiallyDisabledVetOrSurvivngSpouse,
		ownedThroughCooperativeHousing,
		ownedThroughNonProfit,
		specialFieldsValue,
		currentMarketValue,
	]);

	const buttonDisabled = React.useMemo(() => {
		if (flowId === "CALIFORNIA_EXEMPTION") {
			return [
				hasDisability,
				isVeteran,
				isInheritedProperty,
				ownedThroughNonProfit,
				specialFieldsValue?.is_active_duty_personnel,
				noneOfTheAbove,
			].every(value => !value);
		}

		if (flowId === "GEORGIA_EXEMPTION") {
			return [
				isVeteran,
				specialFieldsValue?.surviving_spouse_or_child_of_a_veteran,
				survivingSpouseOfFirstResponderKilledInLineOfDuty,
				specialFieldsValue?.is_active_duty_personnel,
				noneOfTheAbove,
			].every(value => !value);
		}

		if (flowId === "NEW_YORK_EXEMPTION") {
			return [
				isVeteran,
				hasDisability,
				specialFieldsValue?.is_manufactured_home,
				ownedThroughCooperativeHousing,
				specialFieldsValue?.is_active_duty_personnel,
				noneOfTheAbove,
			].every(value => !value);
		}

		if (flowId === "ILLINOIS_EXEMPTION") {
			return [
				hasDisability,
				specialFieldsValue?.is_65_years_or_older,
				specialFieldsValue?.disabled_veteran,
				specialFieldsValue?.recently_returned_from_active_duty,
				specialFieldsValue?.was_resident_of_assisted_living_facility,
				specialFieldsValue?.is_active_duty_personnel,
				noneOfTheAbove,
			].every(value => !value);
		}

		if (flowId === "FLORIDA_EXEMPTION") {
			return [
				hasDisability,
				specialFieldsValue?.is_widow,
				specialFieldsValue?.is_blind,
				specialFieldsValue?.is_disabled_first_responder_or_surviving_spouse_of_a_first_responder,
				specialFieldsValue?.is_surviving_spouse_of_a_first_responder_who_died_in_the_line_of_duty,
				specialFieldsValue?.is_a_disabled_veteran_aged_65_or_older_or_a_surviving_spouse_of_a_disabled_veteran,
				specialFieldsValue?.is_a_disabled_veteran_confined_to_a_wheelchair,
				specialFieldsValue?.is_a_totally_and_permanently_disabled_veteran_or_a_surviving_spouse_of_such_a_veteran,
				specialFieldsValue?.is_a_surviving_spouse_of_a_veteran_who_died_while_on_active_duty,
				specialFieldsValue?.is_active_duty_personnel,
				noneOfTheAbove,
			].every(value => !value);
		}

		if (flowId === "MICHIGAN_EXEMPTION") {
			return [
				specialFieldsValue?.make_less_than_67k,
				specialFieldsValue?.is_65_years_or_older,
				specialFieldsValue?.disabled_veteran,
				specialFieldsValue?.surviving_spouse_of_a_veteran,
				specialFieldsValue?.is_commercial_property,
				specialFieldsValue?.is_active_duty_personnel,
				noneOfTheAbove,
			].every(value => !value);
		}

		if (flowId === "PENNSYLVANIA_EXEMPTION") {
			return [
				specialFieldsValue?.is_age_18_or_older_disabled_and_under_45k_per_year,
				specialFieldsValue?.is_age_65_or_older_and_under_45k_per_year,
				specialFieldsValue?.is_surving_spouse_of_a_deceased_partner_age_50_or_older,
				specialFieldsValue?.is_active_duty_personnel,
				noneOfTheAbove,
			].every(value => !value);
		}

		return [
			hasDisability,
			collectRentAtThisProperty,
			disabledVeteranOrSurvivingSpouse,
			survivingSpouseOfVeteranKilledInLineOfDuty,
			survivingSpouseOfFirstResponderKilledInLineOfDuty,
			survivingSpouseOf65PlusPerson,
			otherHeirOwnersOccupy,
			donatedResidenceOfPartiallyDisabledVetOrSurvivngSpouse,
			ownedThroughCooperativeHousing,
			isInheritedProperty,
			specialFieldsValue?.is_active_duty_personnel,
			specialFieldsValue?.is_manufactured_home,
			noneOfTheAbove,
		].every(value => !value);
	}, [
		flowId,
		hasDisability,
		isVeteran,
		disabledVeteranOrSurvivingSpouse,
		survivingSpouseOf65PlusPerson,
		survivingSpouseOfVeteranKilledInLineOfDuty,
		survivingSpouseOfFirstResponderKilledInLineOfDuty,
		isInheritedProperty,
		donatedResidenceOfPartiallyDisabledVetOrSurvivngSpouse,
		ownedThroughCooperativeHousing,
		ownedThroughNonProfit,
		specialFieldsValue,
		noneOfTheAbove,
	]);

	return (
		<form
			onSubmit={e => {
				e.preventDefault();
				handleSubmit();
			}}>
			<div className="property-profile-question-card exemption-survey-card">
				<div className="property-profile-question-card-title-container">
					<p>Do any of the following situations apply?</p>
				</div>
				<div className="exemptions-survey-rare-situations-checkbox-section mt-2">
					{checkboxOptions.map(({ label, name, state }, index) => (
						<div key={"rare-situations-" + name + index}>
							<Checkbox
								name={name}
								label={label}
								labelEl="p"
								checked={state ?? false}
								onChange={handleCheckboxChange}
							/>
							{/* {secondaryOption && state && (
									<div className="exemptions-survey-checkbox-secondary">
										<Checkbox
											name={secondaryOption.name}
											label={secondaryOption.label}
											labelEl="p"
											onChange={handleCheckboxChange}
											checked={secondaryOption.state ?? false}
										/>
									</div>
								)} */}
						</div>
					))}
				</div>
			</div>
			{unableToFile ? (
				<p className="sm exemptions-notification-message-rust">
					{flowId === "NEW_YORK_EXEMPTION"
						? "At the moment Ownwell is unable to support this type of exemption or STAR Credit filing in your county. While we're not able to help you with your exemption or STAR Credit application, you can still file yourself directly with the county through the county website."
						: "At the moment Ownwell is unable to support this type of Exemption filing in your county. While we're not able to help you with your Exemptions application, you can still file yourself directly with the county through the county website."}
				</p>
			) : null}
			{!unableToFile &&
				flowId === "MICHIGAN_EXEMPTION" &&
				specialFieldsValue?.make_less_than_67k &&
				currentMarketValue &&
				currentMarketValue < 154_000 && (
					<p className="sm exemptions-notification-message-azure">
						Based on your income and property value, you may qualify for even
						more property tax benefits such as Homestead Credit that we
						currently do not support. You may proceed and we will continue to
						file your PRE, but please note we won’t apply for the Homestead
						Credit. We recommend filing yourself directly with the county.
					</p>
				)}
			{flowId === "PENNSYLVANIA_EXEMPTION" &&
				[
					specialFieldsValue?.is_age_18_or_older_disabled_and_under_45k_per_year,
					specialFieldsValue?.is_age_65_or_older_and_under_45k_per_year,
					specialFieldsValue?.is_surving_spouse_of_a_deceased_partner_age_50_or_older,
				].some(v => !!v) && (
					<p className="sm exemptions-notification-message-azure">
						We can proceed to file your general exemption application, but
						please be aware you may qualify for additional benefits on top of
						your exemption based on the options chosen above. After we’ve filed,
						we recommend contacting the county to see what you may qualify for.
					</p>
				)}
			<ButtonV2
				size="large"
				disabled={buttonDisabled}
				type="submit"
				className="exemptions-continue-button">
				Continue
			</ButtonV2>
		</form>
	);
};

export default RareSituations;
